import React, { useEffect, useState } from 'react';
import AccordionBtn, { AccordionProps } from 'components/commercetools-ui/atoms/accordion';
import useClassNames from 'helpers/hooks/useClassNames';
import { useFormat } from 'helpers/hooks/useFormat';
import Button from 'components/commercetools-ui/atoms/button';
import { AvailableShippingMethods, ShippingMethod } from 'shared/types/cart/CustomShippingMethod';
import Radio from '../../../atoms/radio';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useCart } from 'frontastic';
import { SHIPPING_METHOD_CODE_QUERY_PARAM } from 'helpers/constants/query-params';
import { validateZipcode } from '../utils/validateZipcode';

export interface Props {
  className?: string;
  accordionProps?: AccordionProps;
  onCalculateShipping?: (zipcode: string) => Promise<AvailableShippingMethods | undefined>;
  localShippingData?: AvailableShippingMethods;
  localShippingMethod?: ShippingMethod;
  onShippingMethodChange: (method?: ShippingMethod) => void;
  onChangeAddress: () => void;
  customerZipcode?: string;
}

const ShippingForm: React.FC<Props> = ({
  className,
  accordionProps,
  onCalculateShipping,
  onChangeAddress,
  onShippingMethodChange,
  localShippingData,
  localShippingMethod,
  customerZipcode,
}) => {
  const { formatMessage: formatCartMessage } = useFormat({ name: 'cart' });
  const { data: cart } = useCart();

  const [zipcode, setZipcode] = useState(customerZipcode ?? '');
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [processing, setProcessing] = useState(false);

  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const inputClassName = useClassNames([
    'h-40 w-full border px-12 py-10 body-text placeholder:text-tertiary disabled:bg-neutral-300 focus:outline-none',
    hasError ? 'border-feedback-error text-feedback-error focus:border-feedback-error' : 'border-neutral-300',
  ]);

  const containerClassName = useClassNames([
    'py-16 px-8 body-text leading-[21px] border-t-2 border-stroke-primary text-primary',
    className,
  ]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setZipcode(e.target.value);
    setHasError(false);
  };

  const handleRadioChange = (method?: ShippingMethod) => {
    onShippingMethodChange(method);
    if (method?.code) {
      const params = new URLSearchParams(searchParams.toString());
      params.set(SHIPPING_METHOD_CODE_QUERY_PARAM, method.code);
      router.replace(`${pathname}?${params.toString()}`, { scroll: false });
    }
  };

  const handleCalculateShipping = () => {
    if (processing || !zipcode) return;

    const isValidZipcode = validateZipcode(zipcode);
    if (!isValidZipcode) {
      setErrorMessage(formatCartMessage({ id: 'shipping.zip.error' }));
      setHasError(true);
      return;
    }

    setProcessing(true);

    onCalculateShipping?.(zipcode)
      .then((data) => {
        const shippingMethodCode = searchParams.get(SHIPPING_METHOD_CODE_QUERY_PARAM);
        if (shippingMethodCode) {
          const foundShippingMethod = data?.shippingMethods.find((method) => method.code === shippingMethodCode);
          if (foundShippingMethod) {
            handleRadioChange(foundShippingMethod);
            return;
          }
        }
        handleRadioChange(data?.shippingMethods?.[0]);
      })
      .catch((err: Error) => {
        setErrorMessage(
          err.message ??
            formatCartMessage({
              id: 'shipping.zip.error',
              defaultMessage: 'The Zip code is not valid',
            }),
        );
        setHasError(true);
      })
      .finally(() => {
        setProcessing(false);
      });
  };

  // This recalculates shipping cost when updating the cart
  useEffect(() => {
    if (cart?.lineItems?.length) handleCalculateShipping();
  }, [cart]);

  return (
    <div className={containerClassName}>
      <AccordionBtn
        closedSectionTitle={formatCartMessage({ id: 'shipping.calculate', defaultMessage: 'Calculate Shipping' })}
        buttonClassName="text-primary font-medium"
        variant="plusAndMinus"
        {...accordionProps}
      >
        <div className="mt-24">
          {!localShippingData && (
            <form
              className="relative"
              onSubmit={(e) => {
                e.preventDefault();
                handleCalculateShipping();
              }}
            >
              <input
                className={inputClassName}
                value={zipcode}
                placeholder={formatCartMessage({
                  id: 'shipping.zip.placeholder',
                  defaultMessage: 'ZIP Code',
                })}
                required
                onChange={handleChange}
                disabled={processing}
              />
              {hasError && <p className="body-text-xsmall mt-8 leading-normal text-feedback-error">{errorMessage}</p>}

              {!localShippingData && (
                <Button variant="secondary" className="mt-8 w-full" disabled={processing}>
                  {processing
                    ? formatCartMessage({
                        id: 'shipping.calculating',
                        defaultMessage: 'Calculating...',
                      })
                    : formatCartMessage({
                        id: 'shipping.calculate.button',
                        defaultMessage: 'Calculate Shipping',
                      })}
                </Button>
              )}
            </form>
          )}

          {localShippingData && (
            <div className="flex flex-col gap-y-16">
              <div className="mt-16 flex gap-x-20">
                <p className="body-text">
                  {formatCartMessage({ id: 'shipping.shippingTo', defaultMessage: 'Shipping to' })}
                  <span className="font-medium">{localShippingData.address.zipcode}</span>
                </p>
                <button
                  className="body-text-small hover:underline"
                  onClick={() => {
                    onChangeAddress();
                  }}
                >
                  {formatCartMessage({ id: 'shipping.changeAddress' })}
                </button>
              </div>
              <div className="flex flex-col gap-y-8">
                {localShippingData.shippingMethods.map((method) => {
                  return (
                    <div key={method.method} className="flex gap-x-12">
                      <Radio
                        id={method.method}
                        name="shipping-method"
                        checked={localShippingMethod?.method === method.method}
                        onChange={() => handleRadioChange(method)}
                      >
                        <div className="flex flex-col">
                          <span className="body-text-small font-medium">{method.estimatedShippingDate}</span>
                          <span className="body-text-small">
                            {`${method.estimatedCost ? `${method.estimatedCost} - ${method.method}` : method.method}`}
                          </span>
                        </div>
                      </Radio>
                    </div>
                  );
                })}
              </div>
              <p className="body-text-small text-text-tertiary">
                {formatCartMessage({
                  id: 'shipping.disclaimer',
                  defaultMessage:
                    '* Delivery estimates are based on the date of release and the shipping method you select.',
                })}
              </p>
            </div>
          )}
        </div>
      </AccordionBtn>
    </div>
  );
};

export default ShippingForm;
